"use client";
import React, { useState, useMemo } from "react";
import { cva } from "class-variance-authority";
import { NewsCard } from "@/components/ui/cards/newsCard";
import type { NewsCardItem } from "@/types/cards";
import type { IconName } from "@/types/icons";
import { SlideUpIn } from "@/components/animations";
import { Button } from "@/components/ui/buttons";
import { ThemeVariant } from "@/types";

const newsCardTeaserVariants = cva("container", {
  variants: {
    variant: {
      light: "py-lg",
      dark: "py-lg",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

export interface NewsCardTeaserProps {
  variant?: ThemeVariant;
  cards: NewsCardItem[];
  /** Optional icon for the first (featured) card */
  featuredIcon?: IconName;
  /** Optional label for the first (featured) card */
  featuredLabel?: string | React.ReactNode;
  flowy?: boolean;
  className?: string;
  /** Options for "Load More" functionality */
  loadMore?: {
    /** Initial number of items to show */
    initialCount: number;
    /** Number of items to add on each click */
    step: number;
    /** Label for the button */
    buttonLabel?: string;
  };
}

/**
 * NewsCardTeaser block component that displays news cards in a grid.
 * Similar to ServiceCardList but styled for NewsCards.
 */
export const NewsCardTeaser: React.FC<NewsCardTeaserProps> = ({
  cards,
  featuredIcon,
  featuredLabel,
  variant = "light",
  flowy = true,
  className = "",
  loadMore,
}) => {
  const [visibleCount, setVisibleCount] = useState(
    loadMore?.initialCount ?? cards.length
  );

  const displayedCards = useMemo(
    () => cards.slice(0, visibleCount),
    [cards, visibleCount]
  );

  const hasMore = visibleCount < cards.length;

  const handleLoadMore = () => {
    if (loadMore) {
      setVisibleCount((prev) => Math.min(prev + loadMore.step, cards.length));
    }
  };

  const animationDelayIncrement = 0.1;

  const renderCard = (
    card: NewsCardItem,
    index: number,
    isFullWidth = false
  ) => {
    return (
      <SlideUpIn
        key={`news-card-slide-${index}`}
        delay={index * animationDelayIncrement}
        className={
          isFullWidth
            ? "col-span-12"
            : "col-span-12 md:col-span-6 lg:col-span-4"
        }
      >
        <NewsCard
          image={card.image}
          icon={isFullWidth ? "fire" : card.icon}
          headline={card.headline}
          label={isFullWidth ? "Top Artikel" : card.label}
          href={card.href}
          align={isFullWidth ? "bottom" : "top"}
          aspect={isFullWidth ? "2/1" : card.aspect || "2/3"}
          padding={card.padding || "lg"}
          buttonText={isFullWidth ? "Artikel lesen" : ""}
        />
      </SlideUpIn>
    );
  };

  if (flowy) {
    const firstCard = displayedCards[0];
    const remainingCards = displayedCards.slice(1);

    return (
      <div className={newsCardTeaserVariants({ variant, className })}>
        {/* Full width first card */}
        {firstCard && (
          <div className="col-span-12 mb-md">
            {renderCard(firstCard, 0, true)}
          </div>
        )}

        {/* Existing 3-column flowy grid for remaining cards */}
        {[0, 1, 2].map((colIndex) => (
          <div
            key={`col-${colIndex}`}
            className={`col-span-12 md:col-span-6 lg:col-span-4 flex flex-col gap-md ${
              colIndex === 1 ? "lg:pt-xl" : ""
            }`}
          >
            {remainingCards
              .filter((_, i) => i % 3 === colIndex)
              .map((card, i) => {
                const globalIndex = i * 3 + colIndex + 1;
                return renderCard(card, globalIndex);
              })}
          </div>
        ))}

        {loadMore && hasMore && (
          <div className="col-span-12 flex justify-center mt-xl">
            <Button
              variant="secondary"
              color={variant === "light" ? "yellow" : "purple"}
              onClick={handleLoadMore}
              label={loadMore.buttonLabel}
              icon="arrow-right"
            />
          </div>
        )}

        {/* Hidden content for SEO */}
        <div className="sr-only" aria-hidden="true">
          {cards.slice(visibleCount).map((card, idx) => (
            <div key={`seo-hidden-${idx}`}>
              <h3>{card.headline}</h3>
              <p>{card.label}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={newsCardTeaserVariants({ variant, className })}>
      {displayedCards.map((card, index) => {
        const isFullWidth = index === 0;
        return (
          <div
            key={`grid-item-${index}`}
            className={
              isFullWidth
                ? "col-span-12 mb-md"
                : "col-span-12 md:col-span-6 lg:col-span-4"
            }
          >
            {renderCard(card, index, isFullWidth)}
          </div>
        );
      })}

      {loadMore && hasMore && (
        <div className="col-span-12 flex justify-center mt-xl">
          <Button
            variant="secondary"
            color={variant === "light" ? "yellow" : "purple"}
            onClick={handleLoadMore}
            label={loadMore.buttonLabel || "Mehr laden"}
            icon="arrow-right"
          />
        </div>
      )}

      {/* Hidden content for SEO */}
      <div className="sr-only" aria-hidden="true">
        {cards.slice(visibleCount).map((card, idx) => (
          <div key={`seo-hidden-${idx}`}>
            <h3>{card.headline}</h3>
            <p>{card.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
